import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as TaskActions from './task.actions';
import { TasksService } from 'src/app/services/tasks.service';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { TranslateService } from '@ngx-translate/core';
import * as NotificationsActions from '../notifications/notifications.actions';

@Injectable()
export class TaskEffects {
    getTasks$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.getTasks),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('user', 'currentTechnician'),
            this.store.select('task', 'paginationParams'),
        ),
        switchMap(([action, device, currentTechnician, taskPaginationParams]) => {
            if (!device) {
                return of(TaskActions.getTasksFailed({ errorMessage: '' }));
            }
            let userId = action.userId;
            if (currentTechnician) {
                userId = currentTechnician.PersonifiedId;
            }
            let paging = taskPaginationParams;
            if (action.paging) {
                paging = action.paging;
            }
            return this.tasksService.getTasks(device.Id, false, true, userId, paging).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return TaskActions.getTasksSuccess({
                            paginationParams: paging, paging: resp.Data.Paging, tasks: resp.Data.Items
                        });
                    } else {
                        return TaskActions.getTasksFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(TaskActions.getTasksFailed({ errorMessage: error.message }));
                })
            );
        })
    ));
    getActiveTasks$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.getActiveTasks),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('user', 'currentTechnician'),
        ),
        switchMap(([action, device, currentTechnician]) => {
            if (!device) {
                return of(TaskActions.getActiveTasksFailed({ errorMessage: '' }));
            }
            let userId = action.userId;
            if (currentTechnician) {
                userId = currentTechnician.PersonifiedId;
            }
            return this.tasksService.getTasks(device.Id, true, false, userId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return TaskActions.getActiveTasksSuccess({
                            tasks: resp.Data.Items
                        });
                    } else {
                        return TaskActions.getActiveTasksFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(TaskActions.getActiveTasksFailed({ errorMessage: error.message }));
                })
            );
        })
    ));

    getTaskStates$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.getTaskStates),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, lang]) => this.tasksService.getTaskStates(lang.code).pipe(
            map((resp) => {
                if (resp.Success) {
                    return TaskActions.getTaskStatesSuccess({ states: resp.Data.Items });
                } else {
                    return TaskActions.getTaskStatesFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(TaskActions.getTaskStatesFailed({ errorMessage: error.message }));
            })
        ))
    ));

    getTaskTypes$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.getTaskTypes),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, lang]) => this.tasksService.getTaskTypes(lang.code).pipe(
            map((resp) => {
                if (resp.Success) {
                    return TaskActions.getTaskTypesSuccess({ types: resp.Data.Items });
                } else {
                    return TaskActions.getTaskTypesFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(TaskActions.getTaskTypesFailed({ errorMessage: error.message }));
            })
        ))
    ));

    abortTask$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.abortTask),
        withLatestFrom(this.store.select('task', 'paginationParams')),
        switchMap(([action, paging]) => this.tasksService.abortTask(action.taskId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return TaskActions.abortTaskSuccess({ message: this.translate.instant('TASK_ABORTED_SUCCESFULLY') });
                } else {
                    return TaskActions.abortTaskFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(TaskActions.abortTaskFailed({ errorMessage: error.message }));
            })
        ))
    ));

    reloadTasksAfterAbort$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.abortTaskSuccess),
        switchMap(() => [
            TaskActions.getTasks({})
        ])
    ));



    getServiceStatus$ = createEffect(() => this.actions$.pipe(
        ofType(TaskActions.getServiceStatus),
        switchMap((action) => this.tasksService.getServiceStatus().pipe(
            map((resp) => {
                return TaskActions.getServiceStatusSuccess({ statusData: resp });
            }),
            catchError(error => {
                return of(TaskActions.getServiceStatusFailed({ errorMessage: error.message }));
            })
        ))
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            TaskActions.abortTaskSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("TASK", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            TaskActions.abortTaskFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("TASK", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private tasksService: TasksService,
        private translate: TranslateService,
    ) { }
}
